/* eslint-disable max-lines-per-function */
import { BERECHNUNGSGRUNDLAGE_DREI_MONATS_DURCHSCHNITT } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/berechnungsgrundlageDreiMonatsDurchschnitt';
import { DYNAMISCHE_ABWESENHEIT_ARTEN } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/dynamischeAbwesenheitstypen';
import {
  Arbeitstage,
  arbeitstage,
} from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/tage';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function useAbwesenheitenSchema() {
  const { t } = useTranslation();
  const AbwesenheitenAllgemeinSchema = z
    .object({
      anzahlUrlaubstage: z
        .string()
        .min(1, {
          error: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.anzahlUrlaubstage.validationError',
          ),
        })
        .refine(
          (val) => {
            const rest = Math.abs(Number(val.replace(',', '.')) % 1);
            return rest === 0 || rest === 0.5;
          },
          {
            error: t(
              'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.anzahlUrlaubstage.validationErrorFormat',
            ),
          },
        ),
      anzahlArbeitstage: z
        .custom<keyof Arbeitstage>()
        .optional()
        .refine((val) => val && Object.keys(arbeitstage).includes(val), {
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.validationError',
          ),
        }),
      stundenBewertung: z
        .enum(['fest', 'dynamisch'])
        .optional()
        .refine((val) => !!val, {
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.stundenbewertung.validationError',
          ),
        }),
      stunden: z.string().optional(),
      dynamisch: z.enum(Object.values(DYNAMISCHE_ABWESENHEIT_ARTEN)).optional(),
      dynamisch_wochenschnitt_start: z.string().optional(),
      dynamisch_wochenschnitt_limitieren: z.boolean().optional(),
      dynamisch_drei_monatsschnitt_berechnungsgrundlage: z
        .enum(Object.values(BERECHNUNGSGRUNDLAGE_DREI_MONATS_DURCHSCHNITT))
        .optional(),
    })
    // eslint-disable-next-line complexity
    .superRefine((data, ctx) => {
      if (data.stundenBewertung === 'fest' && !data.stunden) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.stunden.validationError',
          ),
          path: ['stunden'],
        });
      } else if (data.stundenBewertung === 'dynamisch') {
        if (!data.dynamisch) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.validationError',
            ),
            path: ['dynamisch'],
          });
        } else {
          switch (data.dynamisch) {
            case 'wochenschnitt':
              if (!data.dynamisch_wochenschnitt_start) {
                ctx.addIssue({
                  code: 'custom',
                  message: t(
                    'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.wochendurchschnitt.start_abwesenheitszeit.validationError',
                  ),
                  path: ['dynamisch_wochenschnitt_start'],
                });
              }
              break;

            case 'drei_monatsschnitt':
              if (!data.dynamisch_drei_monatsschnitt_berechnungsgrundlage) {
                ctx.addIssue({
                  code: 'custom',
                  message: t(
                    'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.drei_monatsdurchschnitt.berechnungsgrundlage.validationError',
                  ),
                  path: ['dynamisch_drei_monatsschnitt_berechnungsgrundlage'],
                });
              }
              break;

            default:
              break;
          }
        }
      }
    });

  const FeiertagseinstellungenSchema = z.object({
    zuschlagsregelungGreift: z.boolean(),
    stundenGutschreibung: z.boolean(),
    taeglicheArbeitszeitAuffuellen: z.boolean(),
  });

  const GuttageSchema = z.object({
    guttageBewerten: z.boolean(),
  });

  return {
    AbwesenheitenAllgemeinSchema,
    FeiertagseinstellungenSchema,
    GuttageSchema,
  };
}
